import Button from "@/components/Button";
import { dropdownArrow, openLinkIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";

import { CompletedStepIcon } from "../CompletedStepIcon";
import { TutorialAlert } from "../TutorialAlert";

interface FirstPartSectionProps {
  step1Completed: boolean;
  step2Completed: boolean;
  step3Completed: boolean;
  showInstallPrompt: boolean;
  isFirstPartCollapsed: boolean;
  isLoggedIn: boolean;
  setIsFirstPartCollapsed: (collapsed: boolean) => void;
  onLogin: () => void;
  handleGetChromeExtension: () => void;
}

export const FirstPartSection = ({
  step1Completed,
  step2Completed,
  step3Completed,
  showInstallPrompt,
  isFirstPartCollapsed,
  setIsFirstPartCollapsed,
  isLoggedIn,
  onLogin,
  handleGetChromeExtension,
}: FirstPartSectionProps) => {
  const { t } = useTranslation("clubImport");
  /*
    The steps are a sequence: the extension can only be installed in a browser
    that takes extensions, and it can only import for an account. Showing a step
    as available before the one it depends on is cleared invites a click that
    cannot work, so each stays locked until its predecessor is done.
  */
  const step2Locked = !step1Completed;
  const step3Locked = !step1Completed || !step2Completed;

  return (
    <section
      className={cn(
        "mb-12 p-12 bg-panel-background rounded-3xl overflow-hidden transition-all ",
        isFirstPartCollapsed && "cursor-pointer",
      )}
      onClick={() => {
        if (isFirstPartCollapsed) {
          setIsFirstPartCollapsed(false);
        }
      }}
    >
      {/* Collapsible Header */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          setIsFirstPartCollapsed(!isFirstPartCollapsed);
        }}
        className="w-full flex items-center justify-between mb-4 transition-opacity cursor-pointer"
      >
        <div className="flex items-center gap-x-2">
          <h2 className="text-white text-xl font-semibold">
            {t("tutorial.firstPart.title")}
          </h2>
          <CompletedStepIcon
            completed={step1Completed && step2Completed && step3Completed}
          />
        </div>
        <div
          className={cn(
            "transition-transform duration-300 ease-in-out [&_svg_path]:stroke-white",
            isFirstPartCollapsed && "rotate-180",
          )}
        >
          {dropdownArrow}
        </div>
      </div>

      {/* Collapsible Content */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isFirstPartCollapsed ? "max-h-0" : "max-h-[2000px]",
        )}
      >
        <div>
          <p className="text-dark-tertiary text-sm mb-8 max-w-3xl">
            {t("tutorial.firstPart.intro")}
          </p>

          {/* Steps Container - flex-wrap */}
          <div className="flex flex-wrap gap-6">
            {/* Step 1 */}
            <div
              className={cn(
                "w-[316px] flex-shrink-0",
                step1Completed && "opacity-50 pointer-events-none",
              )}
            >
              {/* Image placeholder */}
              {/* <img
                className="w-[316px] h-[289px] mb-4 flex items-center justify-center"
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/import-tutorial/desktop/chrome.png`}
              /> */}
              <div className="flex items-center gap-x-2 mb-4">
                <h3 className="text-white font-semibold">
                  {t("step", { n: 1 })}
                </h3>
                {/*
                  The only step the user cannot clear from this page — nothing
                  here fixes the browser they arrived in — so an unmet step 1
                  is an error rather than something still outstanding.
                */}
                <CompletedStepIcon completed={step1Completed} failed />
              </div>
              <p className="text-dark-secondary text-sm mb-4">
                {t("tutorial.firstPart.step1")}
              </p>
            </div>

            {/* Step 2 */}
            <div
              className={cn(
                "w-[316px] flex-shrink-0",
                (step2Completed || step2Locked) &&
                  "opacity-50 pointer-events-none",
              )}
            >
              {/* Image placeholder */}
              {/* <img
                className="w-[316px] h-[289px] mb-4 flex items-center justify-center"
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/import-tutorial/desktop/login-sub.png`}
              /> */}
              <div className="flex items-center gap-x-2 mb-4">
                <h3 className="text-white font-semibold">
                  {t("step", { n: 2 })}
                </h3>
                <CompletedStepIcon completed={step2Completed} />
              </div>
              <p className="text-dark-secondary text-sm mb-4">
                {t("tutorial.firstPart.step2")}
              </p>
              <div className="flex gap-x-2">
                <Button
                  variant={isLoggedIn || step2Locked ? "disabled" : "primary"}
                  label={t("tutorial.firstPart.step2Cta")}
                  onClick={onLogin}
                />
              </div>
            </div>

            {/* Step 3 */}
            <div
              className={cn(
                "w-[316px] flex-shrink-0",
                (step3Completed || step3Locked) &&
                  "opacity-50 pointer-events-none",
              )}
            >
              {/* Image placeholder */}
              {/* <img
                className="w-[316px] h-[289px] mb-4 flex items-center justify-center"
                src={
                  import.meta.env.VITE_APP_ASSETS_BASE_URL +
                  "/import-tutorial/desktop/chrome-x-sbc.png"
                }
              /> */}
              <div className="flex items-center gap-x-2 mb-4">
                <h3 className="text-white font-semibold">
                  {t("step", { n: 3 })}
                </h3>
                <CompletedStepIcon completed={step3Completed} />
              </div>
              <p className="text-dark-secondary text-sm mb-4">
                {t("tutorial.firstPart.step3")}
              </p>
              <div>
                <Button
                  variant={step3Locked ? "disabled" : "primary"}
                  label={t("tutorial.firstPart.step3Cta")}
                  icon={<div className="size-3">{openLinkIcon}</div>}
                  onClick={handleGetChromeExtension}
                />
              </div>
              {/*
                Not while the step is locked: the prompt is remembered across
                visits, so a logged-out returner would otherwise get one inside
                a greyed-out step they cannot act on yet.
              */}
              {showInstallPrompt && !step3Locked && (
                <TutorialAlert
                  title={t("tutorial.extensionMissing.title")}
                  className="mt-4"
                >
                  <p className="text-sm leading-5 text-dark-secondary">
                    {t("tutorial.extensionMissing.body")}
                  </p>
                </TutorialAlert>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
